import { useState } from 'react';
import { ChevronDown, ChevronRight, ExternalLink } from 'lucide-react';
import { PROSPECT_PAIN_TYPE_LABELS, type ProspectPainTypeValue } from '@crm/shared';
import { Badge, Button, Card } from '../../../ui';
import type { ProspectSignal } from '../../../services/prospect-signals.service';

interface SignalCardProps {
  signal: ProspectSignal;
  onPromote: (signal: ProspectSignal) => void;
  onDismiss: (signal: ProspectSignal) => void;
  isBusy?: boolean;
}

/**
 * `painType` chega do backend como nome do enum do Prisma
 * (`MANTER_DESENVOLVIMENTO`), enquanto a querystring usa kebab-case
 * (`manter-desenvolvimento`). Esta é a única ponte entre os dois formatos no
 * frontend. Valor desconhecido cai para o nome cru em vez de virar vazio —
 * enum novo no backend não deve apagar a informação da tela.
 */
function painTypeLabel(enumName: string): string {
  const kebab = enumName.toLowerCase().replace(/_/g, '-') as ProspectPainTypeValue;
  return PROSPECT_PAIN_TYPE_LABELS[kebab] ?? enumName;
}

function scoreTone(score: number): 'accent' | 'info' | 'neutral' {
  if (score >= 85) {
    return 'accent';
  }
  return score >= 70 ? 'info' : 'neutral';
}

export function SignalCard({ signal, onPromote, onDismiss, isBusy = false }: SignalCardProps) {
  const [showReasoning, setShowReasoning] = useState(false);

  return (
    <Card className="flex flex-col gap-3">
      <div className="flex items-center gap-2 flex-wrap">
        <Badge tone={scoreTone(signal.score)} size="md">
          {signal.score}
        </Badge>
        <Badge tone="neutral">{painTypeLabel(signal.painType)}</Badge>
        <span className="text-sm text-ink-secondary">
          @{signal.authorHandle}
          {signal.communityName ? ` · ${signal.communityName}` : ''}
        </span>
      </div>

      <blockquote className="border-l-2 border-border pl-3 text-sm text-ink-primary italic">
        “{signal.evidenceQuote}”
      </blockquote>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button
          type="button"
          onClick={() => setShowReasoning((v) => !v)}
          aria-expanded={showReasoning}
          className="inline-flex items-center gap-1 text-xs text-ink-secondary hover:text-ink-primary"
        >
          {showReasoning ? (
            <ChevronDown className="w-3 h-3" />
          ) : (
            <ChevronRight className="w-3 h-3" />
          )}
          por que {signal.score}
        </button>

        <a
          href={signal.sourceUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-xs text-ink-secondary hover:text-ink-primary"
        >
          ver no Skool
          <ExternalLink className="w-3 h-3" />
        </a>
      </div>

      {showReasoning && <p className="text-sm text-ink-secondary">{signal.reasoning}</p>}

      <div className="flex items-center justify-end gap-2">
        <Button variant="secondary" disabled={isBusy} onClick={() => onDismiss(signal)}>
          Dispensar
        </Button>
        <Button disabled={isBusy} onClick={() => onPromote(signal)}>
          Promover
        </Button>
      </div>
    </Card>
  );
}
